

export const frontMatter = {
	title: 'Solving IndexedDB Slowness for Seamless Apps',
	slug: 'slow-indexeddb.html',
	description: 'Struggling with IndexedDB performance? Discover hidden bottlenecks, advanced tuning techniques, and next-gen storage like the File System Access API.',
	image: '/headers/slow-indexeddb.jpg'
};
export const contentTitle = 'Why IndexedDB is slow and what to use instead';
export {default as metadata} from '@site/.docusaurus/docusaurus-plugin-content-docs/default/site-docs-slow-indexeddb-md-8b0.json'
export const assets = {

};

import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {CenteredImage} from '@site/src/components/centered-image';
export const toc = [{
  "value": "Batched Cursor",
  "id": "batched-cursor",
  "level": 2
}, {
  "value": "IndexedDB Sharding",
  "id": "indexeddb-sharding",
  "level": 2
}, {
  "value": "Custom Indexes",
  "id": "custom-indexes",
  "level": 2
}, {
  "value": "Relaxed durability",
  "id": "relaxed-durability",
  "level": 2
}, {
  "value": "Explicit transaction commits",
  "id": "explicit-transaction-commits",
  "level": 2
}, {
  "value": "In-Memory on top of IndexedDB",
  "id": "in-memory-on-top-of-indexeddb",
  "level": 2
}, {
  "value": "In-Memory: Persistence",
  "id": "in-memory-persistence",
  "level": 3
}, {
  "value": "In-Memory: Multi Tab Support",
  "id": "in-memory-multi-tab-support",
  "level": 3
}, {
  "value": "Further read",
  "id": "further-read",
  "level": 2
}];
function _createMdxContent(props) {
  const _components = {
    a: "a",
    admonition: "admonition",
    blockquote: "blockquote",
    code: "code",
    figure: "figure",
    h1: "h1",
    h2: "h2",
    h3: "h3",
    header: "header",
    li: "li",
    p: "p",
    pre: "pre",
    span: "span",
    strong: "strong",
    ul: "ul",
    ..._provideComponents(),
    ...props.components
  }, {RxdbLogo} = _components;
  if (!RxdbLogo) _missingMdxReference("RxdbLogo", true);
  return _jsxs(_Fragment, {
    children: [_jsx(_components.header, {
      children: _jsx(_components.h1, {
        id: "why-indexeddb-is-slow-and-what-to-use-instead",
        children: "Why IndexedDB is slow and what to use instead"
      })
    }), "\n", _jsxs(_components.p, {
      children: ["So you have a JavaScript web application that needs to store data at the client side, either to make it ", _jsx(_components.a, {
        href: "/offline-first.html",
        children: "offline usable"
      }), ", just for caching purposes or for other reasons."]
    }), "\n", _jsxs(_components.p, {
      children: ["For ", _jsx(_components.a, {
        href: "/articles/browser-database.html",
        children: "in-browser data storage"
      }), ", you have some options:"]
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Cookies"
        }), " are sent with each HTTP request, so you cannot store more than a few strings in them."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "WebSQL"
        }), " ", _jsx(_components.a, {
          href: "https://hacks.mozilla.org/2010/06/beyond-html5-database-apis-and-the-road-to-indexeddb/",
          children: "is deprecated"
        }), " because it never was a real standard and turning it into a standard would have been too difficult."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.a, {
          href: "/articles/localstorage.html",
          children: "LocalStorage"
        }), " is a synchronous API over asynchronous IO-access. Storing and reading data can fully block the JavaScript process so you cannot use LocalStorage for more than few simple key-value pairs."]
      }), "\n", _jsxs(_components.li, {
        children: ["The ", _jsx(_components.strong, {
          children: "FileSystem API"
        }), " could be used to store plain binary files, but it is ", _jsx(_components.a, {
          href: "https://caniuse.com/filesystem",
          children: "only supported in chrome"
        }), " for now."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "IndexedDB"
        }), " is an indexed key-object database. It can store json data and iterate over its indexes. It is ", _jsx(_components.a, {
          href: "https://caniuse.com/indexeddb",
          children: "widely supported"
        }), " and stable."]
      }), "\n"]
    }), "\n", _jsxs(_components.admonition, {
      title: "UPDATE April 2023",
      type: "note",
      children: [_jsxs(_components.p, {
        children: ["Since beginning of 2023, all modern browsers ship the ", _jsx(_components.strong, {
          children: "File System Access API"
        }), " which allows to persistently store data in the browser with a way better performance. For ", _jsx(_components.a, {
          href: "https://rxdb.info/",
          children: "RxDB"
        }), " you can use the ", _jsx(_components.a, {
          href: "/rx-storage-opfs.html",
          children: "OPFS RxStorage"
        }), " to get about 4x performance improvement compared to IndexedDB."]
      }), _jsx(RxdbLogo, {
        alt: "IndexedDB Database",
        width: 250
      })]
    }), "\n", _jsxs(_components.p, {
      children: ["It becomes clear that the only way to go is IndexedDB. You start developing your app and everything goes fine.\nBut as soon as your app gets bigger, more complex or just handles more data, you might notice something. ", _jsx(_components.strong, {
        children: "IndexedDB is slow"
      }), ". Not slow like a database on a cheap server, ", _jsx(_components.strong, {
        children: "even slower"
      }), "! Inserting a few hundred documents can take up several seconds. Time which can be critical for a fast page load. Even sending data over the internet to the backend can be faster than storing it inside of an IndexedDB database."]
    }), "\n", _jsxs(_components.blockquote, {
      children: ["\n", _jsx(_components.p, {
        children: "Transactions vs Throughput"
      }), "\n"]
    }), "\n", _jsxs(_components.p, {
      children: ["So before we start complaining, lets analyze what exactly is slow. When you run tests on Nolans ", _jsx(_components.a, {
        href: "http://nolanlawson.github.io/database-comparison/",
        children: "Browser Database Comparison"
      }), " you can see that inserting 1k documents into IndexedDB takes about 80 milliseconds, 0.08ms per document. This is not really slow. It is quite fast and it is very unlikely that you want to store that many document at the same time at the client side. But the key point here is that all these documents get written in a ", _jsx(_components.code, {
        children: "single transaction"
      }), "."]
    }), "\n", _jsxs(_components.p, {
      children: ["I forked the comparison tool ", _jsx(_components.a, {
        href: "https://pubkey.github.io/client-side-databases/database-comparison/index.html",
        children: "here"
      }), " and changed it to use one transaction per document write. And there we have it. Inserting 1k documents with one transaction per write, takes about 2 seconds. Interestingly if we increase the document size to be 100x bigger, it still takes about the same time to store them. This makes clear that the limiting factor to IndexedDB performance is the transaction handling, not the data throughput."]
    }), "\n", _jsx(CenteredImage, {
      src: "./files/indexeddb-transaction-throughput.png",
      alt: "IndexedDB transaction throughput",
      width: 700
    }), "\n", _jsxs(_components.p, {
      children: ["To fix your IndexedDB performance problems you have to make sure to use as less data transfers/transactions as possible.\nSometimes this is easy, as instead of iterating over a documents list and calling single inserts, with RxDB you could use the ", _jsx(_components.a, {
        href: "https://rxdb.info/rx-collection.html#bulkinsert",
        children: "bulk methods"
      }), " to store many document at once.\nBut most of the time is not so easy. Your user clicks around, data gets replicated from the backend, another browser tab writes data. All these things can happen at random time and you cannot crunch all that data in a single transaction."]
    }), "\n", _jsxs(_components.p, {
      children: ["Another solution is to just not care about performance at all. In a few releases the browser vendors will have optimized IndexedDB and everything is fast again. Well, IndexedDB was slow ", _jsx(_components.a, {
        href: "https://www.researchgate.net/publication/281065948_Performance_Testing_and_Comparison_of_Client_Side_Databases_Versus_Server_Side",
        children: "in 2013"
      }), " and it is still slow today. If this trend continues, it will still be slow in a few years from now. Waiting is not an option. The chromium devs made ", _jsx(_components.a, {
        href: "https://bugs.chromium.org/p/chromium/issues/detail?id=1025456#c15",
        children: "a statement"
      }), " to focus on optimizing read performance, not write performance."]
    }), "\n", _jsxs(_components.p, {
      children: ["Switching to WebSQL (even if it is deprecated) is also not an option because, like ", _jsx(_components.a, {
        href: "https://pubkey.github.io/client-side-databases/database-comparison/index.html",
        children: "the comparison tool shows"
      }), ", it has even slower transactions."]
    }), "\n", _jsxs(_components.p, {
      children: ["So you need a way to ", _jsx(_components.strong, {
        children: "make IndexedDB faster"
      }), ". Reading less data by ", _jsx(_components.a, {
        href: "/articles/indexeddb/indexeddb-query.html",
        children: "picking the right index for a query"
      }), " is one part of that. In the following I lay out some performance optimizations than can be made to have faster reads and writes in IndexedDB."]
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "HINT:"
      }), " You can reproduce all performance tests ", _jsx(_components.a, {
        href: "https://github.com/pubkey/indexeddb-performance-tests",
        children: "in this repo"
      }), ". In all tests we work on a dataset of 40000 ", _jsx(_components.code, {
        children: "human"
      }), " documents with a random ", _jsx(_components.code, {
        children: "age"
      }), " between ", _jsx(_components.code, {
        children: "1"
      }), " and ", _jsx(_components.code, {
        children: "100"
      }), "."]
    }), "\n", _jsx(_components.h2, {
      id: "batched-cursor",
      children: "Batched Cursor"
    }), "\n", _jsxs(_components.p, {
      children: ["With ", _jsx(_components.a, {
        href: "https://caniuse.com/indexeddb2",
        children: "IndexedDB 2.0"
      }), ", new methods were introduced which can be utilized to improve performance. With the ", _jsx(_components.code, {
        children: "getAll()"
      }), " method, a faster alternative to the old ", _jsx(_components.code, {
        children: "openCursor()"
      }), " can be created which improves performance when reading data from the IndexedDB store."]
    }), "\n", _jsxs(_components.p, {
      children: ["Lets say we want to query all user documents that have an ", _jsx(_components.code, {
        children: "age"
      }), " greater than ", _jsx(_components.code, {
        children: "25"
      }), " out of the store.\nTo implement a fast batched cursor that only needs calls to ", _jsx(_components.code, {
        children: "getAll()"
      }), " and not to ", _jsx(_components.code, {
        children: "getAllKeys()"
      }), ", we first need to create an ", _jsx(_components.code, {
        children: "age"
      }), " index that contains the primary ", _jsx(_components.code, {
        children: "id"
      }), " as last field."]
    }), "\n", _jsx(_components.figure, {
      "data-rehype-pretty-code-figure": "",
      children: _jsx(_components.pre, {
        style: {
          backgroundColor: "var(--shiki-background)",
          color: "var(--shiki-foreground)"
        },
        tabIndex: "0",
        "data-language": "ts",
        "data-theme": "css-variables",
        children: _jsxs(_components.code, {
          "data-language": "ts",
          "data-theme": "css-variables",
          style: {
            display: "grid"
          },
          children: [_jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: "myIndexedDBObjectStore"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-function)"
              },
              children: ".createIndex"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "("
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                color: "var(--shiki-token-string-expression)"
              },
              children: "    'age-index'"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-punctuation)"
              },
              children: ","
            })]
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "    ["
            })
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                color: "var(--shiki-token-string-expression)"
              },
              children: "        'age'"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-punctuation)"
              },
              children: ","
            })]
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-string-expression)"
              },
              children: "        'id'"
            })
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "    ]"
            })
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: ");"
            })
          })]
        })
      })
    }), "\n", _jsxs(_components.p, {
      children: ["This is required because the ", _jsx(_components.code, {
        children: "age"
      }), " field is not unique, and we need a way to checkpoint the last returned batch so we can continue from there in the next call to ", _jsx(_components.code, {
        children: "getAll()"
      }), "."]
    }), "\n", _jsx(_components.figure, {
      "data-rehype-pretty-code-figure": "",
      children: _jsx(_components.pre, {
        style: {
          backgroundColor: "var(--shiki-background)",
          color: "var(--shiki-foreground)"
        },
        tabIndex: "0",
        "data-language": "ts",
        "data-theme": "css-variables",
        children: _jsxs(_components.code, {
          "data-language": "ts",
          "data-theme": "css-variables",
          style: {
            display: "grid"
          },
          children: [_jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: "const"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: " maxAge"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: " ="
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: " 25"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: ";"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: "let"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: " result "
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: "="
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: " [];"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: "const"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: " tx"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: ":"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-function)"
              },
              children: " IDBTransaction"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: " ="
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: " db"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-function)"
              },
              children: ".transaction"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "("
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "    [storeName]"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-punctuation)"
              },
              children: ","
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-string-expression)"
              },
              children: " 'readonly'"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-punctuation)"
              },
              children: ","
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: " TRANSACTION_SETTINGS"
            })]
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: ");"
            })
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: "const"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: " store"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: " ="
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: " tx"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-function)"
              },
              children: ".objectStore"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "(storeName);"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: "const"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: " index"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: " ="
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: " store"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-function)"
              },
              children: ".index"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "("
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-string-expression)"
              },
              children: "'age-index'"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: ");"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: "let"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: " lastDoc;"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: "let"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: " done "
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: "="
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: " false"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: ";"
            })]
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-comment)"
              },
              children: "/**"
            })
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-comment)"
              },
              children: " * Run the batched cursor until all results are retrieved"
            })
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-comment)"
              },
              children: " * or the end of the index is reached."
            })
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-comment)"
              },
              children: " */"
            })
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: "while"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: " (done "
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: "==="
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: " false"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: ") {"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: "    await"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: " new"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: " Promise"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "((res"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-punctuation)"
              },
              children: ","
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: " rej) "
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: "=>"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: " {"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: "        const"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: " range"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: " ="
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: " IDBKeyRange"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-function)"
              },
              children: ".bound"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "("
            })]
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-comment)"
              },
              children: "          /**"
            })
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-comment)"
              },
              children: "           * If we have a previous document as checkpoint,"
            })
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-comment)"
              },
              children: "           * we have to continue from it's age and id values."
            })
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-comment)"
              },
              children: "           */"
            })
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "            ["
            })
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "                lastDoc "
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: "?"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: " lastDoc"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: ".age "
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: ":"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: " -"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: "Infinity"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-punctuation)"
              },
              children: ","
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "                lastDoc "
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: "?"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: " lastDoc"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: ".id "
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: ":"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: " -"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: "Infinity"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-punctuation)"
              },
              children: ","
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "            ]"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-punctuation)"
              },
              children: ","
            })]
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "            ["
            })
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "                maxAge "
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: "+"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: " 0.00000001"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-punctuation)"
              },
              children: ","
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: "                String"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-function)"
              },
              children: ".fromCharCode"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "("
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: "65535"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: ")"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "            ]"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-punctuation)"
              },
              children: ","
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: "            true"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-punctuation)"
              },
              children: ","
            })]
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: "            false"
            })
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "        );"
            })
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: "        const"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: " openCursorRequest"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: " ="
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: " index"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-function)"
              },
              children: ".getAll"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "(range"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-punctuation)"
              },
              children: ","
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: " batchSize);"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: "        openCursorRequest"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "."
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-function)"
              },
              children: "onerror"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: " ="
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: " err "
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: "=>"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-function)"
              },
              children: " rej"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "(err);"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: "        openCursorRequest"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "."
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-function)"
              },
              children: "onsuccess"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: " ="
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: " e "
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: "=>"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: " {"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: "            const"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: " subResult"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: ":"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-function)"
              },
              children: " TestDocument"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "[] "
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: "="
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: " e"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "."
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: "target"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: ".result;"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "            lastDoc "
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: "="
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-function)"
              },
              children: " lastOfArray"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "(subResult);"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: "            if"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: " ("
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: "subResult"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "."
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: "length"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: " ==="
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: " 0"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: ") {"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "                done "
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: "="
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: " true"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: ";"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "            } "
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: "else"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: " {"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "                result "
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: "="
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: " result"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-function)"
              },
              children: ".concat"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "(subResult);"
            })]
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "            }"
            })
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                color: "var(--shiki-token-function)"
              },
              children: "            res"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "();"
            })]
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "        };"
            })
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "    });"
            })
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "}"
            })
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: "console"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-function)"
              },
              children: ".dir"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "(result);"
            })]
          })]
        })
      })
    }), "\n", _jsx("p", {
      align: "center",
      children: _jsx("img", {
        src: "./files/indexeddb-batched-cursor.png",
        alt: "IndexedDB batched cursor",
        width: "100%"
      })
    }), "\n", _jsxs(_components.p, {
      children: ["As the performance test results show, using a batched cursor can give a huge improvement. Interestingly choosing a high batch size is important. When you known that all results of a given ", _jsx(_components.code, {
        children: "IDBKeyRange"
      }), " are needed, you should not set a batch size at all and just directly query all documents via ", _jsx(_components.code, {
        children: "getAll()"
      }), "."]
    }), "\n", _jsxs(_components.p, {
      children: ["RxDB uses batched cursors in the ", _jsx(_components.a, {
        href: "/rx-storage-indexeddb.html",
        children: "IndexedDB RxStorage"
      }), "."]
    }), "\n", _jsx(_components.h2, {
      id: "indexeddb-sharding",
      children: "IndexedDB Sharding"
    }), "\n", _jsxs(_components.p, {
      children: ["Sharding is a technique, normally used in server side databases, where the database is partitioned horizontally. Instead of storing all documents at one table/collection, the documents are split into so called ", _jsx(_components.strong, {
        children: "shards"
      }), " and each shard is stored on one table/collection. This is done in server side architectures to spread the load between multiple physical servers which ", _jsx(_components.strong, {
        children: "increases scalability"
      }), "."]
    }), "\n", _jsxs(_components.p, {
      children: ["When you use IndexedDB in a browser, there is of course no way to split the load between the client and other servers. But you can still benefit from sharding. Partitioning the documents horizontally into ", _jsx(_components.strong, {
        children: "multiple IndexedDB stores"
      }), ", has shown to have a big performance improvement in write- and read operations while only increasing initial page load slightly."]
    }), "\n", _jsx("p", {
      align: "center",
      children: _jsx("img", {
        src: "./files/indexeddb-sharding-performance.png",
        alt: "IndexedDB sharding performance",
        width: "100%"
      })
    }), "\n", _jsxs(_components.p, {
      children: ["As shown in the performance test results, sharding should always be done by ", _jsx(_components.code, {
        children: "IDBObjectStore"
      }), " and not by database. Running a batched cursor over the whole dataset with 10 store shards in parallel is about ", _jsx(_components.strong, {
        children: "28% faster"
      }), " then running it over a single store. Initialization time increases minimal from ", _jsx(_components.code, {
        children: "9"
      }), " to ", _jsx(_components.code, {
        children: "17"
      }), " milliseconds.\nGetting a quarter of the dataset by batched iterating over an index, is even ", _jsx(_components.strong, {
        children: "43%"
      }), " faster with sharding then when a single store is queried."]
    }), "\n", _jsx(_components.p, {
      children: "As downside, getting 10k documents by their id is slower when it has to run over the shards.\nAlso it can be much effort to recombined the results from the different shards into the required query result. When a query without a limit is done, the sharding method might cause a data load huge overhead."
    }), "\n", _jsxs(_components.p, {
      children: ["Sharding can be used with RxDB with the ", _jsx(_components.a, {
        href: "/rx-storage-sharding.html",
        children: "Sharding Plugin"
      }), "."]
    }), "\n", _jsx(_components.h2, {
      id: "custom-indexes",
      children: "Custom Indexes"
    }), "\n", _jsx(_components.p, {
      children: "Indexes improve the query performance of IndexedDB significant. Instead of fetching all data from the storage when you search for a subset of it, you can iterate over the index and stop iterating when all relevant data has been found."
    }), "\n", _jsxs(_components.p, {
      children: ["For example to query for all user documents that have an ", _jsx(_components.code, {
        children: "age"
      }), " greater than ", _jsx(_components.code, {
        children: "25"
      }), ", you would create an ", _jsx(_components.code, {
        children: "age+id"
      }), " index.\nTo be able to run a batched cursor over the index, we always need our primary key (", _jsx(_components.code, {
        children: "id"
      }), ") as the last index field."]
    }), "\n", _jsxs(_components.p, {
      children: ["Instead of doing this, you can use a ", _jsx(_components.code, {
        children: "custom index"
      }), " which can improve the performance. The custom index runs over a helper field ", _jsx(_components.code, {
        children: "ageIdCustomIndex"
      }), " which is added to each document on write. Our index now only contains a single ", _jsx(_components.code, {
        children: "string"
      }), " field instead of two (age-", _jsx(_components.code, {
        children: "number"
      }), " and id-", _jsx(_components.code, {
        children: "string"
      }), ")."]
    }), "\n", _jsx(_components.figure, {
      "data-rehype-pretty-code-figure": "",
      children: _jsx(_components.pre, {
        style: {
          backgroundColor: "var(--shiki-background)",
          color: "var(--shiki-foreground)"
        },
        tabIndex: "0",
        "data-language": "ts",
        "data-theme": "css-variables",
        children: _jsxs(_components.code, {
          "data-language": "ts",
          "data-theme": "css-variables",
          style: {
            display: "grid"
          },
          children: [_jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-comment)"
              },
              children: "// On document insert add the ageIdCustomIndex field."
            })
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: "const"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: " idMaxLength"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: " ="
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: " 20"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "; "
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-comment)"
              },
              children: "// must be known to craft a custom index"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: "docData"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: ".ageIdCustomIndex "
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: "="
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: " docData"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: ".age "
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: "+"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: " docData"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-function)"
              },
              children: "."
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: "id"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-function)"
              },
              children: ".padStart"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "(idMaxLength"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-punctuation)"
              },
              children: ","
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-string-expression)"
              },
              children: " ' '"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: ");"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: "store"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-function)"
              },
              children: ".put"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "(docData);"
            })]
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-comment)"
              },
              children: "// ..."
            })
          })]
        })
      })
    }), "\n", _jsx(_components.figure, {
      "data-rehype-pretty-code-figure": "",
      children: _jsx(_components.pre, {
        style: {
          backgroundColor: "var(--shiki-background)",
          color: "var(--shiki-foreground)"
        },
        tabIndex: "0",
        "data-language": "ts",
        "data-theme": "css-variables",
        children: _jsxs(_components.code, {
          "data-language": "ts",
          "data-theme": "css-variables",
          style: {
            display: "grid"
          },
          children: [_jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-comment)"
              },
              children: "// normal index"
            })
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: "myIndexedDBObjectStore"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-function)"
              },
              children: ".createIndex"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "("
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                color: "var(--shiki-token-string-expression)"
              },
              children: "    'age-index'"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-punctuation)"
              },
              children: ","
            })]
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "    ["
            })
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                color: "var(--shiki-token-string-expression)"
              },
              children: "        'age'"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-punctuation)"
              },
              children: ","
            })]
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-string-expression)"
              },
              children: "        'id'"
            })
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "    ]"
            })
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: ");"
            })
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: " "
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-comment)"
              },
              children: "// custom index"
            })
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: "myIndexedDBObjectStore"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-function)"
              },
              children: ".createIndex"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "("
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                color: "var(--shiki-token-string-expression)"
              },
              children: "    'age-index-custom'"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-punctuation)"
              },
              children: ","
            })]
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "    ["
            })
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-string-expression)"
              },
              children: "        'ageIdCustomIndex'"
            })
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "    ]"
            })
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: ");"
            })
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: " "
          })]
        })
      })
    }), "\n", _jsxs(_components.p, {
      children: ["To iterate over the index, you also use a custom crafted key range, depending on the last batched cursor checkpoint. Therefore the ", _jsx(_components.code, {
        children: "maxLength"
      }), " of ", _jsx(_components.code, {
        children: "id"
      }), " must be known."]
    }), "\n", _jsx(_components.figure, {
      "data-rehype-pretty-code-figure": "",
      children: _jsx(_components.pre, {
        style: {
          backgroundColor: "var(--shiki-background)",
          color: "var(--shiki-foreground)"
        },
        tabIndex: "0",
        "data-language": "ts",
        "data-theme": "css-variables",
        children: _jsxs(_components.code, {
          "data-language": "ts",
          "data-theme": "css-variables",
          style: {
            display: "grid"
          },
          children: [_jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-comment)"
              },
              children: "// key range for normal index"
            })
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: "const"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: " range"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: " ="
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: " IDBKeyRange"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-function)"
              },
              children: ".bound"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "("
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "    ["
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: "25"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-punctuation)"
              },
              children: ","
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-string-expression)"
              },
              children: " ''"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "]"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-punctuation)"
              },
              children: ","
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "    ["
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: "Infinity"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-punctuation)"
              },
              children: ","
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: " Infinity"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "]"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-punctuation)"
              },
              children: ","
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: "    true"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-punctuation)"
              },
              children: ","
            })]
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: "    false"
            })
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: ");"
            })
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: " "
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-comment)"
              },
              children: "// key range for custom index"
            })
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: "const"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: " range"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: " ="
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: " IDBKeyRange"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-function)"
              },
              children: ".bound"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "("
            })]
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-comment)"
              },
              children: "    // combine both values to a single string"
            })
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: "    25"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: " +"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-string-expression)"
              },
              children: " ''"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-function)"
              },
              children: ".padStart"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "(idMaxLength"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-punctuation)"
              },
              children: ","
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-string-expression)"
              },
              children: " ' '"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: ")"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-punctuation)"
              },
              children: ","
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: "    Infinity"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-punctuation)"
              },
              children: ","
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: "    true"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-punctuation)"
              },
              children: ","
            })]
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: "    false"
            })
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: ");"
            })
          })]
        })
      })
    }), "\n", _jsx(CenteredImage, {
      src: "./files/indexeddb-custom-index.png",
      alt: "IndexedDB custom index",
      width: 700
    }), "\n", _jsxs(_components.p, {
      children: ["As shown, using a custom index can further improve the performance of running a batched cursor by about ", _jsx(_components.code, {
        children: "10%"
      }), "."]
    }), "\n", _jsxs(_components.p, {
      children: ["Another big benefit of using custom indexes, is that you can also encode ", _jsx(_components.code, {
        children: "boolean"
      }), " values in them, which ", _jsx(_components.a, {
        href: "https://github.com/w3c/IndexedDB/issues/76",
        children: "cannot be done"
      }), " with normal IndexedDB indexes."]
    }), "\n", _jsxs(_components.p, {
      children: ["RxDB uses custom indexes in the ", _jsx(_components.a, {
        href: "/rx-storage-indexeddb.html",
        children: "IndexedDB RxStorage"
      }), "."]
    }), "\n", _jsx(_components.h2, {
      id: "relaxed-durability",
      children: "Relaxed durability"
    }), "\n", _jsxs(_components.p, {
      children: ["Chromium based browsers allow to set ", _jsx(_components.a, {
        href: "https://developer.mozilla.org/en-US/docs/Web/API/IDBTransaction/durability",
        children: "durability"
      }), " to ", _jsx(_components.code, {
        children: "relaxed"
      }), " when creating an IndexedDB transaction. Which runs the transaction in a less secure durability mode, which can improve the performance."]
    }), "\n", _jsxs(_components.blockquote, {
      children: ["\n", _jsx(_components.p, {
        children: "The user agent may consider that the transaction has successfully committed as soon as all outstanding changes have been written to the operating system, without subsequent verification."
      }), "\n"]
    }), "\n", _jsxs(_components.p, {
      children: ["As shown ", _jsx(_components.a, {
        href: "https://nolanlawson.com/2021/08/22/speeding-up-indexeddb-reads-and-writes/",
        children: "here"
      }), ", using the relaxed durability mode can improve performance slightly. The best performance improvement could be measured when many small transactions have to be run. Less, bigger transaction do not benefit that much."]
    }), "\n", _jsx(_components.h2, {
      id: "explicit-transaction-commits",
      children: "Explicit transaction commits"
    }), "\n", _jsxs(_components.p, {
      children: ["By explicitly committing a transaction, another slight performance improvement can be achieved. Instead of waiting for the browser to commit an open transaction, we call the ", _jsx(_components.code, {
        children: "commit()"
      }), " method to explicitly close it."]
    }), "\n", _jsx(_components.figure, {
      "data-rehype-pretty-code-figure": "",
      children: _jsx(_components.pre, {
        style: {
          backgroundColor: "var(--shiki-background)",
          color: "var(--shiki-foreground)"
        },
        tabIndex: "0",
        "data-language": "ts",
        "data-theme": "css-variables",
        children: _jsxs(_components.code, {
          "data-language": "ts",
          "data-theme": "css-variables",
          style: {
            display: "grid"
          },
          children: [_jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-comment)"
              },
              children: "// .commit() is not available on all browsers, so first check if it exists."
            })
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                color: "var(--shiki-token-keyword)"
              },
              children: "if"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: " ("
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: "transaction"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: ".commit) {"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                color: "var(--shiki-token-constant)"
              },
              children: "    transaction"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-token-function)"
              },
              children: ".commit"
            }), _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "()"
            })]
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                color: "var(--shiki-foreground)"
              },
              children: "}"
            })
          })]
        })
      })
    }), "\n", _jsxs(_components.p, {
      children: ["The improvement of this technique is minimal, but observable as ", _jsx(_components.a, {
        href: "https://nolanlawson.com/2021/08/22/speeding-up-indexeddb-reads-and-writes/",
        children: "these tests"
      }), " show."]
    }), "\n", _jsx(_components.h2, {
      id: "in-memory-on-top-of-indexeddb",
      children: "In-Memory on top of IndexedDB"
    }), "\n", _jsxs(_components.p, {
      children: ["To prevent transaction handling and to fix the performance problems, we need to stop using IndexedDB as a database. Instead all data is loaded into the memory on the initial page load. Here all reads and writes happen in memory which is about 100x faster. Only some time after a write occurred, the memory state is persisted into IndexedDB with a ", _jsx(_components.strong, {
        children: "single write transaction"
      }), ". In this scenario IndexedDB is used as a filesystem, not as a database."]
    }), "\n", _jsx(_components.p, {
      children: "There are some libraries that already do that:"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: ["LokiJS with the ", _jsx(_components.a, {
          href: "https://techfort.github.io/LokiJS/LokiIndexedAdapter.html",
          children: "IndexedDB Adapter"
        })]
      }), "\n", _jsx(_components.li, {
        children: _jsx(_components.a, {
          href: "https://github.com/jlongster/absurd-sql",
          children: "Absurd-SQL"
        })
      }), "\n", _jsxs(_components.li, {
        children: ["SQL.js with the ", _jsx(_components.a, {
          href: "https://emscripten.org/docs/api_reference/Filesystem-API.html#filesystem-api-idbfs",
          children: "Emscripten Filesystem API"
        })]
      }), "\n", _jsx(_components.li, {
        children: _jsx(_components.a, {
          href: "https://duckdb.org/2021/10/29/duckdb-wasm.html",
          children: "DuckDB Wasm"
        })
      }), "\n"]
    }), "\n", _jsx(_components.h3, {
      id: "in-memory-persistence",
      children: "In-Memory: Persistence"
    }), "\n", _jsxs(_components.p, {
      children: ["One downside of not directly using IndexedDB, is that your data is not persistent all the time. And when the JavaScript process exists without having persisted to IndexedDB, data can be lost. To prevent this from happening, we have to ensure that the in-memory state is written down to the disc. One point is make persisting as fast as possible. LokiJS for example has the ", _jsx(_components.code, {
        children: "incremental-indexeddb-adapter"
      }), " which only saves new writes to the disc instead of persisting the whole state. Another point is to run the persisting at the correct point in time. For example the RxDB ", _jsx(_components.a, {
        href: "https://rxdb.info/rx-storage-lokijs.html",
        children: "LokiJS storage"
      }), " persists in the following situations:"]
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "When the database is idle and no write or query is running. In that time we can persist the state if any new writes appeared before."
      }), "\n", _jsxs(_components.li, {
        children: ["When the ", _jsx(_components.code, {
          children: "window"
        }), " fires the ", _jsx(_components.a, {
          href: "https://developer.mozilla.org/en-US/docs/Web/API/WindowEventHandlers/onbeforeunload",
          children: "beforeunload event"
        }), " we can assume that the JavaScript process is exited any moment and we have to persist the state. After ", _jsx(_components.code, {
          children: "beforeunload"
        }), " there are several seconds time which are sufficient to store all new changes. This has shown to work quite reliable."]
      }), "\n"]
    }), "\n", _jsx(_components.p, {
      children: "The only missing event that can happen is when the browser exists unexpectedly like when it crashes or when the power of the computer is shut of."
    }), "\n", _jsx(_components.h3, {
      id: "in-memory-multi-tab-support",
      children: "In-Memory: Multi Tab Support"
    }), "\n", _jsxs(_components.p, {
      children: ["One big difference between a web application and a 'normal' app, is that your users can use the app in multiple browser tabs at the same time. But when you have all database state in memory and only periodically write it to disc, multiple browser tabs could overwrite each other and you would loose data. This might not be a problem when you rely on a client-server ", _jsx(_components.a, {
        href: "/replication.html",
        children: "replication"
      }), ", because the lost data might already be replicated with the backend and therefore with the other tabs. But this would not work when the client is offline."]
    }), "\n", _jsxs(_components.p, {
      children: ["The ideal way to solve that problem, is to use a ", _jsx(_components.a, {
        href: "https://developer.mozilla.org/en/docs/Web/API/SharedWorker",
        children: "SharedWorker"
      }), ". A ", _jsx(_components.a, {
        href: "/rx-storage-shared-worker.html",
        children: "SharedWorker"
      }), " is like a ", _jsx(_components.a, {
        href: "https://developer.mozilla.org/en/docs/Web/API/Web_Workers_API",
        children: "WebWorker"
      }), " that runs its own JavaScript process only that the SharedWorker is shared between multiple contexts. You could create the database in the SharedWorker and then all browser tabs could request the Worker for data instead of having their own database. But unfortunately the SharedWorker API does ", _jsx(_components.a, {
        href: "https://caniuse.com/sharedworkers",
        children: "not work"
      }), " in all browsers. Safari ", _jsx(_components.a, {
        href: "https://bugs.webkit.org/show_bug.cgi?id=140344",
        children: "dropped"
      }), " its support and InternetExplorer or Android Chrome, never adopted it. Also it cannot be polyfilled. ", _jsx(_components.strong, {
        children: "UPDATE:"
      }), " ", _jsx(_components.a, {
        href: "https://developer.apple.com/safari/technology-preview/release-notes/",
        children: "Apple added SharedWorkers back in Safari 142"
      })]
    }), "\n", _jsxs(_components.p, {
      children: ["Instead, we could use the ", _jsx(_components.a, {
        href: "https://developer.mozilla.org/en-US/docs/Web/API/Broadcast_Channel_API",
        children: "BroadcastChannel API"
      }), " to communicate between tabs and then apply a ", _jsx(_components.a, {
        href: "https://github.com/pubkey/broadcast-channel#using-the-leaderelection",
        children: "leader election"
      }), " between them. The ", _jsx(_components.a, {
        href: "/leader-election.html",
        children: "leader election"
      }), " ensures that, no matter how many tabs are open, always one tab is the ", _jsx(_components.code, {
        children: "Leader"
      }), "."]
    }), "\n", _jsx(CenteredImage, {
      src: "./files/leader-election.gif",
      alt: "Leader Election",
      width: 500
    }), "\n", _jsx(_components.p, {
      children: "The disadvantage is that the leader election process takes some time on the initial page load (about 150 milliseconds). Also the leader election can break when a JavaScript process is fully blocked for a longer time. When this happens, a good way is to just reload the browser tab to restart the election process."
    }), "\n", _jsx(_components.h2, {
      id: "further-read",
      children: "Further read"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: _jsx(_components.a, {
          href: "https://github.com/pubkey/client-side-databases",
          children: "Offline First Database Comparison"
        })
      }), "\n", _jsx(_components.li, {
        children: _jsx(_components.a, {
          href: "https://nolanlawson.com/2021/08/22/speeding-up-indexeddb-reads-and-writes/",
          children: "Speeding up IndexedDB reads and writes"
        })
      }), "\n", _jsx(_components.li, {
        children: _jsx(_components.a, {
          href: "https://hackaday.com/2021/08/24/sqlite-on-the-web-absurd-sql/",
          children: "SQLITE ON THE WEB: ABSURD-SQL"
        })
      }), "\n", _jsx(_components.li, {
        children: _jsx(_components.a, {
          href: "https://anita-app.com/blog/articles/sqlite-in-a-pwa-with-file-system-access-api.html",
          children: "SQLite in a PWA with FileSystemAccessAPI"
        })
      }), "\n", _jsx(_components.li, {
        children: _jsx(_components.a, {
          href: "https://ravendb.net/articles/re-why-indexeddb-is-slow-and-what-to-use-instead",
          children: "Response to this article by Oren Eini"
        })
      }), "\n"]
    })]
  });
}
export default function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = {
    ..._provideComponents(),
    ...props.components
  };
  return MDXLayout ? _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  }) : _createMdxContent(props);
}
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}

